/**
 * 保有期間 `/reception/admin/retention`(reception:admin。設計書 §10.1、計画 第 7 段)。
 * 規則の一覧、今実行したら処理する件数(プレビュー)、手動実行。
 * 日数(来社履歴・取り消した来訪)を変えられるのは suite:admin で、設定画面の reception.retention で変える。
 * 手動実行は POST なので、読取専用ライセンスの間は機能の門が断る(readOnly で押せなくする)。
 * その間も状態遷移と保有期間のジョブは機能キーなしで自動で回る(§8.1)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Modal, Space, Table, Typography, message } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";

import { PageHeader, Section, TimeAgo } from "../../ui";
import {
  errorText,
  useInvalidateReception,
  useReceptionPerms,
  useRetention,
  useRetentionPreview,
  useSend,
  type RetentionCounts,
  type RetentionInfo,
} from "./hooks";

const { Text } = Typography;

/** 件数のキーの表示(プレビューと実行の結果で共通。知らないキーはキー名のまま出す) */
const COUNT_LABEL: Record<string, string> = {
  anonymize_visits: "来訪の匿名化",
  anonymize_visitors: "来訪者の匿名化",
  anonymized_visits: "匿名化した来訪",
  anonymized_visitors: "匿名化した来訪者",
  waiting: "受付処理中のため次回に回した来訪",
  delivery_purge: "配送素材の消去",
  notifications: "通知・送信の試行の削除",
  kiosk_events: "端末イベントの削除",
  quarantine: "隔離記録の削除",
  claim_tickets: "受付チケットの削除",
  throttle: "試行制限の記録の削除",
  enroll_codes: "端末登録コードの削除",
  idempotency: "二重送信防止キーの削除",
  lifecycle_no_show: "「来訪なし」への切り替え",
  lifecycle_completed: "「終了」への切り替え",
  lifecycle_batches: "状態の切り替えの回数(バッチ)",
  stats_rows: "集計に残した行",
};
const ORDER = Object.keys(COUNT_LABEL);

type Rule = { key: string; what: string; from: string; days: string; how: string };

function rules(r: RetentionInfo): Rule[] {
  const f = r.fixed;
  return [
    { key: "visit", what: "来訪者と来訪", from: "来訪日", days: `${r.retention_days} 日`, how: "氏名・会社・メール・件名などを空にする(記録は残る)" },
    {
      key: "cancelled",
      what: "取り消した来訪・担当者が決まらなかった飛び込み",
      from: "来訪日",
      days: `${r.cancelled_days} 日`,
      how: "同上",
    },
    { key: "delivery", what: "配送素材(招待のリンク・QR の元)", from: "来訪の終了", days: `${f.delivery_days} 日`, how: "NULL にする" },
    { key: "notification", what: "通知の行・送信の試行", from: "終端(送信済み・失敗など)", days: `${f.notification_days} 日`, how: "削除" },
    { key: "kiosk", what: "端末イベント", from: "記録", days: `${f.kiosk_event_days} 日`, how: "削除" },
    { key: "quarantine", what: "隔離記録", from: "記録", days: `${f.quarantine_days} 日`, how: "削除" },
    { key: "ticket", what: "受付チケット", from: "期限", days: `${f.ticket_days} 日`, how: "削除" },
    { key: "throttle", what: "試行制限", from: "期限", days: `${f.throttle_days} 日`, how: "削除" },
  ];
}

function countRows(c: RetentionCounts) {
  const keys = [...ORDER.filter((k) => k in c), ...Object.keys(c).filter((k) => !ORDER.includes(k)).sort()];
  return keys.map((k) => ({ key: k, label: COUNT_LABEL[k] ?? k, count: c[k] }));
}

function CountsTable({ counts }: { counts: RetentionCounts }) {
  return (
    <Table
      scroll={{ x: "max-content" }}
      size="small"
      rowKey="key"
      pagination={false}
      dataSource={countRows(counts)}
      columns={[
        { title: "処理", width: 240, dataIndex: "label" },
        {
          title: "件数",
          dataIndex: "count",
          width: 100,
          align: "right" as const,
          render: (n: number) => <span className="ui-num">{n}</span>,
        },
      ]}
      locale={{ emptyText: "—" }}
      style={{ maxWidth: 520 }}
    />
  );
}

export function RetentionPage() {
  const info = useRetention();
  const preview = useRetentionPreview();
  const send = useSend();
  const { readOnly } = useReceptionPerms();
  const invalidate = useInvalidateReception();
  const [result, setResult] = useState<{ at: string; counts: RetentionCounts } | null>(null);

  const run = useMutation({
    mutationFn: () => send<{ result: RetentionCounts }>("POST", "/retention/run"),
    onSuccess: (r) => {
      setResult({ at: new Date().toISOString(), counts: r.result });
      invalidate();
      void message.success("保有期間の処理を実行しました");
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const confirmRun = () =>
    Modal.confirm({
      title: "保有期間の処理を今すぐ実行しますか?",
      content: "期間を過ぎた来訪者の個人情報を消し、古い記録を削除します。消したものは元に戻せません。",
      okText: "実行する",
      okButtonProps: { danger: true },
      cancelText: "戻る",
      onOk: () => run.mutateAsync(),
    });

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="保有期間"
          description="期間を過ぎた来訪者の個人情報と古い記録は、1 時間ごとに自動で消す。ここでは規則・今消える件数を確かめ、手動で実行できる。"
          primary={
            <Button type="primary" danger loading={run.isPending} disabled={readOnly} onClick={confirmRun}>
              今すぐ実行
            </Button>
          }
        />
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="バックアップから戻した後は、ここで手動実行してください。"
          description="戻したデータには、期間を過ぎて消したはずの個人情報が含まれることがあります。"
        />
        {info.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(info.error)} />}

        <Section
          title="規則"
          extra={
            info.data?.can_edit_days ? (
              <Link to="/reception/admin/settings">日数は設定画面(保持期間)で変える</Link>
            ) : (
              <Text type="secondary">日数を変えられるのはスイート管理者</Text>
            )
          }
        >
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            loading={info.isLoading}
            dataSource={info.data ? rules(info.data) : []}
            columns={[
              { title: "何を", dataIndex: "what", width: 280 },
              { title: "いつから", dataIndex: "from", width: 180 },
              { title: "何日で", dataIndex: "days", width: 160 },
              { title: "どう消すか", width: 320, ellipsis: true, dataIndex: "how" },
            ]}
            scroll={{ x: "max-content" }}
          />
        </Section>

        <Section
          title="今実行したら処理する件数"
          extra={
            <Space>
              {preview.data && (
                <Text type="secondary">
                  <TimeAgo iso={preview.data.at} /> 時点
                </Text>
              )}
              <Button size="small" loading={preview.isFetching} onClick={() => void preview.refetch()}>
                再計算
              </Button>
            </Space>
          }
        >
          {preview.isError ? (
            <Alert type="error" showIcon message={errorText(preview.error)} />
          ) : (
            <CountsTable counts={preview.data?.counts ?? {}} />
          )}
        </Section>

        {result && (
          <Section
            title="実行の結果"
            extra={
              <Text type="secondary">
                <TimeAgo iso={result.at} />
              </Text>
            }
          >
            <CountsTable counts={result.counts} />
          </Section>
        )}
      </div>
    </div>
  );
}
